import React from 'react';
import type {ReactNode} from 'react';

import {observer} from 'mobx-react-lite';

import {FormsDataContext} from '../contexts';

import {useGlobalFormData} from './useGlobalFormData';

/**
 * Props interface for the GlobalFormProvider component.
 * This interface defines the expected properties that can be passed to the GlobalFormProvider component.
 * It ensures type safety and provides clear documentation for component usage.
 */
interface ComponentProps {
    /**
     * The child components that will have access to the global form context.
     * These children can be any valid React node including elements, components, or fragments.
     * The children will be wrapped with the FormsDataContext provider to enable form data access throughout the component tree.
     */
    children: ReactNode;
}

/**
 * A global form provider component that wraps the application to provide form data context to all child components.
 * This component uses MobX observer pattern to ensure reactive updates when form data changes.
 * It serves as the top-level provider for managing global form state across the entire application.
 *
 * @param props          The component props.
 * @param props.children The child components that will have access to the global form context.
 * @returns A JSX element that provides form data context to its children.
 *
 * @example
 * ```tsx
 * <GlobalFormProvider>
 *   <App />
 * </GlobalFormProvider>
 * ```
 */
const GlobalFormProvider = observer(({children}: ComponentProps) => {
    const {formsDataContext} = useGlobalFormData();

    return (
        <FormsDataContext.Provider value={formsDataContext}>
            {children}
        </FormsDataContext.Provider>
    );
});

GlobalFormProvider.displayName = 'GlobalFormProvider';

export {GlobalFormProvider};